import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Send, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

// Base course options shown in the primary dropdown
const BASE_COURSE_OPTIONS = [
  "Adult MHFA — Two Day",
  "MHFA Refresher",
  "MHFA Aware",
  "12-Month Support Package",
  "Lunch & Learn workshops",
  "Mindfulness workshops",
  "Resilient Behaviour workshops",
  "Other / not sure yet",
];

// Courses that also require choosing an "intent" (open course vs in-house)
const REQUIRES_INTENT = ["Adult MHFA — Two Day", "MHFA Refresher"];

const INTENT_OPTIONS = [
  { value: "open", label: "Booking an open course spot" },
  { value: "inhouse", label: "In-house / private group training" },
  { value: "not-sure", label: "Not sure yet" },
];

function parseCourseParam(raw) {
  if (!raw) return { course: "", intent: "" };
  const inhouseMatch = raw.match(/^(.*?)\s*\((?:in[-\s]?house[^)]*)\)$/i);
  if (inhouseMatch) return { course: inhouseMatch[1].trim(), intent: "inhouse" };
  const openMatch = raw.match(/^(.*?)\s*\((?:open[^)]*)\)$/i);
  if (openMatch) return { course: openMatch[1].trim(), intent: "open" };
  return { course: raw.trim(), intent: "" };
}

function humanIntentLabel(value) {
  const found = INTENT_OPTIONS.find((o) => o.value === value);
  return found ? found.label : "";
}

export default function ContactForm() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    organisation: "",
    course: "",
    intent: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const raw = searchParams.get("course");
    const note = searchParams.get("note");
    if (!raw && !note) return;
    const { course, intent } = raw ? parseCourseParam(raw) : { course: "", intent: "" };
    const matchedCourse = course
      ? BASE_COURSE_OPTIONS.find((opt) => opt.toLowerCase() === course.toLowerCase()) || course
      : "";
    setForm((f) => ({
      ...f,
      course: matchedCourse || f.course,
      intent: REQUIRES_INTENT.includes(matchedCourse) ? intent : "",
      message: note ? note : f.message,
    }));
  }, [searchParams]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      const next = { ...f, [name]: value };
      if (name === "course" && !REQUIRES_INTENT.includes(value)) {
        next.intent = "";
      }
      return next;
    });
  };

  const needsIntent = REQUIRES_INTENT.includes(form.course);

  // All fields mandatory
  const missingFields = () => {
    const missing = [];
    if (!form.name.trim()) missing.push("Your name");
    if (!form.email.trim()) missing.push("Email");
    if (!form.organisation.trim()) missing.push("Organisation");
    if (!form.course) missing.push("Course of interest");
    if (needsIntent && !form.intent) missing.push("Type of booking");
    if (!form.message.trim()) missing.push("Message");
    return missing;
  };

  const buildThankYouMessage = () => {
    if (!form.course) {
      return "Thank you — we'll be in touch within one working day.";
    }
    if (needsIntent && form.intent === "inhouse") {
      return `Thank you — we'll reply within one working day about your in-house ${form.course} enquiry.`;
    }
    if (needsIntent && form.intent === "open") {
      return `Thank you — we'll reply within one working day about booking a spot on the ${form.course} open course.`;
    }
    if (needsIntent && form.intent === "not-sure") {
      return `Thank you — we'll reply within one working day and help you figure out the best fit for ${form.course}.`;
    }
    return `Thank you — we'll reply within one working day about your ${form.course} enquiry.`;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const missing = missingFields();
    if (missing.length > 0) {
      toast.error(`Please complete: ${missing.join(", ")}.`);
      return;
    }
    setSubmitting(true);

    // Build a mailto: link with all the enquiry details pre-populated in the
    // visitor's own email client — no server-side email service needed.
    const intentLabel = form.intent ? humanIntentLabel(form.intent) : "";
    const subject = `Enquiry from ${form.name} — ${form.course}${intentLabel ? ` (${intentLabel})` : ""}`;
    const bodyLines = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Organisation: ${form.organisation}`,
      `Course of interest: ${form.course}`,
      ...(intentLabel ? [`Type of booking: ${intentLabel}`] : []),
      "",
      "Message:",
      form.message,
      "",
      "— Sent from the Rise Learning website",
    ];
    const body = bodyLines.join("\n");
    const mailto = `mailto:Liz@rise-learning.co.uk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    // Open in the user's default mail client
    window.location.href = mailto;

    // Show intent-aware confirmation
    toast.success(buildThankYouMessage() + " Your email app should now be open — please press send.", { duration: 8000 });
    setForm({ name: "", email: "", organisation: "", course: "", intent: "", message: "" });
    setSubmitting(false);
  };

  return (
    <section data-testid="contact-section" className="section relative">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <p className="eyebrow mb-4">Get in touch</p>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02] tracking-tight text-[#1E2A25]">
              Let&apos;s build a <br />
              <span className="italic text-[#4A5D52]">healthier team</span> together.
            </h2>
            <p className="mt-8 text-lg text-[#4A5D52] leading-relaxed max-w-md">
              Whether you&apos;re booking a spot on an open course or arranging bespoke training for
              your team, we personally reply to every enquiry — usually within one working day.
            </p>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-full bg-[#4A5D52]/10 text-[#4A5D52] flex items-center justify-center">
                  <Mail className="w-4 h-4" />
                </span>
                <div>
                  <div className="text-sm text-[#4A5D52]">Email</div>
                  <a href="mailto:Liz@rise-learning.co.uk" data-testid="contact-email" className="text-[#1E2A25] hover:text-[#D88C76] transition-colors">Liz@rise-learning.co.uk</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-full bg-[#4A5D52]/10 text-[#4A5D52] flex items-center justify-center">
                  <Phone className="w-4 h-4" />
                </span>
                <div>
                  <div className="text-sm text-[#4A5D52]">Phone</div>
                  <a href="tel:+441462743761" data-testid="contact-phone" className="text-[#1E2A25] hover:text-[#D88C76] transition-colors block">+44 (0) 1462 743761</a>
                  <a href="tel:+447957281194" data-testid="contact-mobile" className="text-sm text-[#4A5D52] hover:text-[#D88C76] transition-colors">Mobile: +44 (0) 7957 281194</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-full bg-[#4A5D52]/10 text-[#4A5D52] flex items-center justify-center">
                  <MapPin className="w-4 h-4" />
                </span>
                <div>
                  <div className="text-sm text-[#4A5D52]">Location</div>
                  <div className="text-[#1E2A25]">UK-wide · Online & In-person</div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 bg-[#F3EFEA] rounded-3xl p-8 md:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Your name *" name="name" value={form.name} onChange={onChange} testId="contact-input-name" />
              <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} testId="contact-input-email" />
              <Field label="Organisation *" name="organisation" value={form.organisation} onChange={onChange} testId="contact-input-organisation" />
              <div className="flex flex-col gap-2">
                <label className="eyebrow text-[#4A5D52]">Course of interest *</label>
                <select
                  name="course"
                  value={form.course}
                  onChange={onChange}
                  data-testid="contact-input-course"
                  className="bg-transparent border-b border-[#4A5D52]/30 py-3 text-[#1E2A25] focus:border-[#4A5D52] outline-none transition-colors"
                >
                  <option value="">Select an option</option>
                  {BASE_COURSE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            {needsIntent && (
              <div className="mt-5">
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}
                  className="flex flex-col gap-2"
                >
                  <label className="eyebrow text-[#4A5D52]">Type of booking *</label>
                  <select
                    name="intent"
                    value={form.intent}
                    onChange={onChange}
                    data-testid="contact-input-intent"
                    className="bg-transparent border-b border-[#4A5D52]/30 py-3 text-[#1E2A25] focus:border-[#4A5D52] outline-none transition-colors"
                  >
                    <option value="">Select an option</option>
                    {INTENT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </motion.div>
              </div>
            )}

            <div className="mt-6 flex flex-col gap-2">
              <label className="eyebrow text-[#4A5D52]">Tell us a little about your needs *</label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={5}
                data-testid="contact-input-message"
                className="bg-transparent border-b border-[#4A5D52]/30 py-3 text-[#1E2A25] focus:border-[#4A5D52] outline-none transition-colors resize-none"
                placeholder="Team size, timings, in-person or online, anything else…"
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              disabled={submitting}
              className="btn-primary mt-10 disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send enquiry"} <Send className="w-4 h-4" />
            </button>
            <p className="mt-4 text-xs text-[#4A5D52]/80">By submitting, you agree to be contacted regarding your enquiry. We never share your details.</p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, testId }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="eyebrow text-[#4A5D52]">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="bg-transparent border-b border-[#4A5D52]/30 py-3 text-[#1E2A25] focus:border-[#4A5D52] outline-none transition-colors"
      />
    </div>
  );
}
